import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { siteStats, siteTagline } from '@/mocks/site';

const quickTags = ['临阵换角', '版权署名', '空白期', '时间线整理', '资料考据'];

export default function Hero() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState('');

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/topics?q=${encodeURIComponent(q)}` : '/topics');
  };

  return (
    <section className="relative w-full overflow-hidden border-b border-background-200 bg-background-100">
      <div className="pointer-events-none absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20layered%20aged%20paper%20texture%20with%20faint%20handwritten%20manuscript%20lines%20and%20warm%20amber%20light%2C%20soft%20vignette%2C%20muted%20cream%20tones%2C%20editorial%20archival%20atmosphere%2C%20no%20people%2C%20subtle%20grain&width=1600&height=900&seq=hero-archive-01&orientation=landscape"
          alt="档案纸张纹理背景，用于突出独家黑料档案中心的资料氛围"
          title="独家黑料档案中心 · 档案纸张纹理"
          className="h-full w-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/85 to-background-50/40"></div>
      </div>

      <div className="relative mx-auto grid max-w-content grid-cols-1 gap-12 px-4 py-14 md:px-8 md:py-20 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:py-24">
        <div className="animate-rise">
          <p className="flex items-center gap-2 text-xs font-medium tracking-[0.32em] text-primary-700">
            <span className="inline-block h-px w-8 bg-primary-500"></span>
            独家黑料 · 档案中心
          </p>

          <h1 className="mt-5">
            <span className="block font-heading text-sm font-semibold tracking-[0.3em] text-foreground-500 md:text-base">
              独家黑料档案中心
            </span>
            <span className="mt-4 block font-heading text-3xl font-black leading-tight text-foreground-950 md:text-[42px] md:leading-[1.2]">
              把散落的旧事，
              <br />
              理回一条能读懂的时间线
            </span>
          </h1>

          <p className="mt-6 max-w-xl text-sm leading-relaxed text-foreground-600 md:text-base">
            收录人物库与历史记录，从源头梳理每一段旧事背景。我们把公开的通讯稿、节目单与场记单按日期拼回去，
            补全碎片化信息，让你不必依赖单一说法，也能看懂事件的完整逻辑。
          </p>

          <form
            onSubmit={handleSearch}
            className="mt-8 flex w-full max-w-xl flex-col gap-3 sm:flex-row"
            role="search"
            aria-label="档案检索"
          >
            <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-3">
              <i className="ri-search-line text-base text-foreground-400" aria-hidden="true"></i>
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="输入人物、事件或关键词，例如“临阵换角”"
                aria-label="检索档案"
                className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
              />
            </div>
            <button
              type="submit"
              className="whitespace-nowrap rounded-md bg-primary-700 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-800"
            >
              检索档案
            </button>
          </form>

          <div className="mt-5 flex flex-wrap items-center gap-2">
            <span className="text-xs text-foreground-500">热门词条</span>
            {quickTags.map((tag) => (
              <Link
                key={tag}
                to={`/topics?q=${encodeURIComponent(tag)}`}
                className="whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-3 py-1 text-xs text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-800"
              >
                {tag}
              </Link>
            ))}
          </div>

          <dl className="mt-10 grid grid-cols-2 gap-x-6 gap-y-6 border-t border-background-300 pt-8 sm:grid-cols-4">
            {siteStats.map((stat) => (
              <div key={stat.label}>
                <dt className="text-xs text-foreground-500">{stat.label}</dt>
                <dd className="mt-1 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                  {stat.value}
                  <span className="ml-1 text-sm font-medium text-primary-700">{stat.unit}</span>
                </dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="animate-rise delay-2 grid grid-cols-2 gap-4">
          <div className="col-span-2 overflow-hidden rounded-lg border border-background-200 bg-background-50">
            <img
              src="https://readdy.ai/api/search-image?query=Editorial%20still%20life%20of%20stacked%20archive%20folders%20with%20paper%20labels%20and%20a%20brass%20desk%20lamp%2C%20warm%20amber%20light%2C%20aged%20cream%20tones%2C%20quiet%20research%20mood%2C%20no%20people&width=900&height=560&seq=hero-desk-02&orientation=landscape"
              alt="档案资料夹与台灯的工作台场景，象征独家黑料档案整理过程"
              title="档案整理台 · 独家黑料档案中心"
              className="h-44 w-full object-top object-cover md:h-52"
            />
          </div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-100 text-accent-700">
              <i className="ri-history-line text-lg" aria-hidden="true"></i>
            </span>
            <p className="mt-3 font-heading text-sm font-semibold text-foreground-950">时间线重组</p>
            <p className="mt-1 text-xs leading-relaxed text-foreground-600">
              用日期把碎片摆开，空白期一目了然。
            </p>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
              <i className="ri-file-search-line text-lg" aria-hidden="true"></i>
            </span>
            <p className="mt-3 font-heading text-sm font-semibold text-foreground-950">版本比对</p>
            <p className="mt-1 text-xs leading-relaxed text-foreground-600">
              六稿并存，差异逐条标注来源。
            </p>
          </div>
          <div className="col-span-2 flex items-center justify-between gap-4 rounded-lg border border-primary-200 bg-primary-100 px-5 py-4">
            <div>
              <p className="font-heading text-sm font-semibold text-primary-900">{siteTagline}</p>
              <p className="mt-1 text-xs text-primary-800">人物库 · 历史记录 · 秘史背景，一处可查</p>
            </div>
            <Link
              to="/figures"
              className="whitespace-nowrap rounded-md bg-primary-700 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-800"
            >
              进入人物库
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}